import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import Button from 'devextreme-react/button';
import DropDownButton from 'devextreme-react/drop-down-button';
import { useHtmlThemeMode } from '../theme/useHtmlThemeMode';
import { EditorWorkspaceOpenFiles } from './EditorWorkspaceOpenFiles';
import { confirmCloseDirtyTabs, editorTabKindIcon } from './editorWorkspaceTabIcons';
import { workspaceHref } from './editorRoutes';
import type { EditorTab, EditorWorkspacePane } from './editorWorkspaceIds';
import { tabsInPane, useEditorWorkspaceStore } from './useEditorWorkspaceStore';
import './editorWorkspaceTabs.css';

export interface EditorWorkspaceTabsProps {
  filesLinkTo?: string;
  pane?: EditorWorkspacePane;
}

type MenuRow = {
  id: string;
  text: string;
  disabled?: boolean;
  beginGroup?: boolean;
};

function navigateAfterClose(navigate: (to: string) => void, filesLinkTo: string, nextId: string | null) {
  if (nextId) navigate(workspaceHref(nextId));
  else navigate(filesLinkTo);
}

export function EditorWorkspaceTabs({
  filesLinkTo = '/files',
  pane = 'primary'
}: EditorWorkspaceTabsProps) {
  const navigate = useNavigate();
  const tabs = useEditorWorkspaceStore((state) => state.tabs);
  const activeTabId = useEditorWorkspaceStore((state) => state.activeTabId);
  const split = useEditorWorkspaceStore((state) => state.split);
  const focusedPane = useEditorWorkspaceStore((state) => state.focusedPane);
  const focusTab = useEditorWorkspaceStore((state) => state.focusTab);
  const closeTab = useEditorWorkspaceStore((state) => state.closeTab);
  const closeOthers = useEditorWorkspaceStore((state) => state.closeOthers);
  const closeToTheRight = useEditorWorkspaceStore((state) => state.closeToTheRight);
  const closeAll = useEditorWorkspaceStore((state) => state.closeAll);
  const splitRight = useEditorWorkspaceStore((state) => state.splitRight);
  const moveToPane = useEditorWorkspaceStore((state) => state.moveToPane);
  const unsplit = useEditorWorkspaceStore((state) => state.unsplit);

  const htmlTheme = useHtmlThemeMode();
  const paneTabs = useMemo(() => tabsInPane(tabs, pane), [pane, tabs]);
  const paneActive =
    paneTabs.find((tab) => tab.id === activeTabId) ??
    (focusedPane === pane ? paneTabs[0] : undefined) ??
    paneTabs[0];

  const go = useCallback(
    (nextId: string | null) => navigateAfterClose(navigate, filesLinkTo, nextId),
    [filesLinkTo, navigate]
  );

  const onSelect = useCallback(
    (tabId: string) => {
      focusTab(tabId);
      navigate(workspaceHref(tabId));
    },
    [focusTab, navigate]
  );

  const requestCloseTab = useCallback(
    (tab: EditorTab) => {
      if (!confirmCloseDirtyTabs([tab], `Close "${tab.title}" with unsaved changes?`)) return;
      go(closeTab(tab.id));
    },
    [closeTab, go]
  );

  const menuItems = useMemo((): MenuRow[] => {
    const rightCount = paneActive
      ? paneTabs.slice(paneTabs.findIndex((tab) => tab.id === paneActive.id) + 1).length
      : 0;
    return [
      { id: 'close', text: 'Close', disabled: !paneActive },
      { id: 'close-others', text: 'Close others', disabled: tabs.length < 2 || !paneActive },
      { id: 'close-right', text: 'Close to the right', disabled: rightCount === 0 },
      { id: 'close-all', text: 'Close all', disabled: tabs.length === 0 },
      {
        id: 'split-right',
        text: 'Split right',
        disabled: !paneActive || split,
        beginGroup: true
      },
      {
        id: 'move-pane',
        text: pane === 'primary' ? 'Move to right pane' : 'Move to left pane',
        disabled: !paneActive || !split
      },
      { id: 'unsplit', text: 'Unsplit', disabled: !split }
    ].filter((item) => {
      if (item.id === 'split-right') return !split;
      if (item.id === 'move-pane' || item.id === 'unsplit') return split;
      return true;
    });
  }, [pane, paneActive, paneTabs, split, tabs.length]);

  const onMenuClick = useCallback(
    (id: string) => {
      if (id === 'unsplit') {
        unsplit();
        return;
      }
      if (!paneActive) return;
      if (id === 'close') {
        requestCloseTab(paneActive);
        return;
      }
      if (id === 'close-others') {
        const others = tabs.filter((tab) => tab.id !== paneActive.id);
        if (!confirmCloseDirtyTabs(others, 'Close other tabs? Unsaved changes will be lost.')) return;
        go(closeOthers(paneActive.id));
        return;
      }
      if (id === 'close-right') {
        const index = paneTabs.findIndex((tab) => tab.id === paneActive.id);
        const right = paneTabs.slice(index + 1);
        if (!confirmCloseDirtyTabs(right, 'Close tabs to the right? Unsaved changes will be lost.')) {
          return;
        }
        go(closeToTheRight(paneActive.id));
        return;
      }
      if (id === 'close-all') {
        if (!confirmCloseDirtyTabs(tabs, 'Close all tabs? Unsaved changes will be lost.')) return;
        closeAll();
        navigate(filesLinkTo);
        return;
      }
      if (id === 'split-right') {
        splitRight(paneActive.id);
        navigate(workspaceHref(paneActive.id));
        return;
      }
      if (id === 'move-pane') {
        const otherPane: EditorWorkspacePane = pane === 'primary' ? 'secondary' : 'primary';
        moveToPane(paneActive.id, otherPane);
        navigate(workspaceHref(paneActive.id));
      }
    },
    [
      closeAll,
      closeOthers,
      closeToTheRight,
      filesLinkTo,
      go,
      moveToPane,
      navigate,
      pane,
      paneActive,
      paneTabs,
      requestCloseTab,
      splitRight,
      tabs,
      unsplit
    ]
  );

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      className="editor-workspace-tabs"
      data-theme={htmlTheme}
      role="tablist"
      aria-label={`${pane} editor tabs`}
    >
      <DropDownButton
        key={`tab-menu-${htmlTheme}`}
        className="editor-workspace-tabs__menu"
        icon="menu"
        stylingMode="text"
        hint="Tab manager"
        displayExpr="text"
        keyExpr="id"
        dropDownOptions={{ width: 220, showTitle: false }}
        items={menuItems}
        onItemClick={(e) => {
          const id = e.itemData?.id;
          if (typeof id === 'string') onMenuClick(id);
        }}
      />
      <div className="editor-workspace-tabs__list">
        {paneTabs.map((tab) => {
          const active = tab.id === (paneActive?.id ?? activeTabId);
          return (
            <div
              key={tab.id}
              role="tab"
              tabIndex={0}
              aria-selected={active}
              className={`editor-workspace-tabs__tab${active ? ' editor-workspace-tabs__tab--active' : ''}`}
              onClick={() => onSelect(tab.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(tab.id);
                }
              }}
              title={tab.path}
            >
              <span
                className={`dx-icon dx-icon-${editorTabKindIcon(tab.kind, tab.mediaKind)}`}
                aria-hidden
              />
              <span className="editor-workspace-tabs__label">
                {tab.dirty ? '● ' : ''}
                {tab.title}
              </span>
              <button
                type="button"
                className="editor-workspace-tabs__close"
                title="Close tab"
                aria-label={`Close ${tab.title}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  requestCloseTab(tab);
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <div className="editor-workspace-tabs__actions">
        {pane === 'primary' ? <EditorWorkspaceOpenFiles /> : null}
        {pane === 'primary' && !split ? (
          <Button
            key={`split-${htmlTheme}`}
            stylingMode="text"
            icon="textdocument"
            hint="Split right"
            text="Split"
            disabled={!paneActive || paneTabs.length === 0}
            onClick={() => paneActive && onMenuClick('split-right')}
          />
        ) : null}
        {split && pane === 'secondary' ? (
          <Button
            key={`unsplit-${htmlTheme}`}
            stylingMode="text"
            icon="close"
            hint="Unsplit"
            text="Unsplit"
            onClick={() => onMenuClick('unsplit')}
          />
        ) : null}
        <Button
          key={`close-all-${htmlTheme}`}
          stylingMode="text"
          icon="clear"
          hint="Close all tabs"
          text="Close all"
          onClick={() => onMenuClick('close-all')}
        />
      </div>
    </div>
  );
}
